import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { BrowserAuthError, BrowserAuthErrorCodes, InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser'
import { useConfig } from '../../Components/Config/ConfigContext'
import AppLoading from '../Common/AppLoading'
import SessionNotice from '../Auth/SessionNotice'

const REFRESH_INTERVAL_MS = 15 * 60 * 1000
const RETURN_PATH_KEY = 'redirectAfterLogin'

// Renew this long before the access token expires. It has to sit inside MSAL's
// 5-minute refresh offset, or acquireTokenSilent just hands back the cached
// token. A fixed 15-minute interval alone left the token expired for up to
// ~10 minutes, and every poll in that gap was a 401 (IDX10223) in the API log.
const RENEW_BEFORE_EXPIRY_MS = 4 * 60 * 1000

// acquireTokenSilent only goes to the network when the cached token is expired
// or inside MSAL's 5-minute refresh offset, so by the time it throws the token
// we are holding is dead and every API call is already 401-ing. Retry on this
// ladder instead of waiting out the full refresh interval, then say something.
const TRANSIENT_RETRY_DELAYS_MS = [5_000, 15_000, 45_000]

// MSAL throws InteractionRequiredAuthError when the STS asks for an
// interaction, but its hidden-iframe fallback fails with a BrowserAuthError
// when the iframe is blocked or never comes back (third-party cookie blocking,
// Safari ITP, a frame-ancestors CSP) — which is the case this redirect flow
// exists for in the first place. Both mean silent renewal is out of options.
const IFRAME_EXHAUSTED_CODES: string[] = [
  BrowserAuthErrorCodes.monitorWindowTimeout,
  BrowserAuthErrorCodes.blockIframeReload,
  BrowserAuthErrorCodes.hashEmptyError,
  BrowserAuthErrorCodes.emptyWindowError,
  BrowserAuthErrorCodes.iframeClosedPrematurely
]

const needsInteraction = (error: unknown): boolean =>
  error instanceof InteractionRequiredAuthError || (error instanceof BrowserAuthError && IFRAME_EXHAUSTED_CODES.includes(error.errorCode))

interface AuthNotice {
  title: string
  message: string
  actionText: string
  // 'signIn' starts a fresh interactive login (a real user gesture, so it is
  // allowed to redirect); 'retry' re-runs silent renewal; 'reload' re-fetches
  // the client configuration.
  action: 'signIn' | 'retry' | 'reload'
}

const SESSION_EXPIRED_NOTICE: AuthNotice = {
  title: 'Your session has expired',
  message: 'We could not sign you in again automatically. Sign in to pick up where you left off.',
  actionText: 'Sign in again',
  action: 'signIn'
}

const RENEWAL_FAILED_NOTICE: AuthNotice = {
  title: 'Could not renew your session',
  message: 'Microsoft could not be reached to renew your sign-in, so the application cannot load data right now.',
  actionText: 'Try again',
  action: 'retry'
}

const CONFIGURATION_NOTICE: AuthNotice = {
  title: 'Sign-in is not configured',
  message:
    'The application configuration has no authentication scope, so no token can be requested. Reload to fetch it again, and report this if it keeps happening.',
  actionText: 'Reload',
  action: 'reload'
}

interface AuthContextValue {
  accessToken: string | null
  isLoggedIn: boolean
}

const AuthContext = createContext<AuthContextValue>({ accessToken: null, isLoggedIn: false })

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal()
  const config = useConfig()
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [expiresOn, setExpiresOn] = useState<Date | null>(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  // Set while the browser is on its way to Microsoft.
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [notice, setNotice] = useState<AuthNotice | null>(null)
  // Set to a delay to schedule one retry of the silent flow; the effect below
  // owns the timer.
  const [retryDelayMs, setRetryDelayMs] = useState<number | null>(null)

  // One automatic interactive attempt per page load, and it is never re-armed.
  // MSAL emits ACQUIRE_TOKEN_FAILURE on a failed redirect, which puts
  // inProgress back to None and re-runs the effect below, so clearing this on
  // failure turned a broken redirect into an endless bounce to Microsoft.
  // Recovery from there is the notice's button, which is a user gesture.
  const interactiveAttempted = useRef(false)
  const transientFailures = useRef(0)

  const fetchToken = useCallback(async () => {
    if (!config || accounts.length === 0) return
    // Starting a second interaction while MSAL is mid-flight throws
    // interaction_in_progress, which is how the timer used to trip over the
    // redirect handling on a fresh sign-in.
    if (inProgress !== InteractionStatus.None) return
    // Already handed over to the operator; nothing automatic left to try.
    if (interactiveAttempted.current) return

    if (!config.authScope) {
      console.error('[Auth] Client configuration has no authScope, cannot request a token.')
      setNotice(CONFIGURATION_NOTICE)
      return
    }

    try {
      const response = await instance.acquireTokenSilent({
        scopes: [config.authScope],
        account: accounts[0]
      })
      transientFailures.current = 0
      setAccessToken(response.accessToken)
      setExpiresOn(response.expiresOn)
      setIsLoggedIn(true)
      setNotice(null)
      return
    } catch (e) {
      if (!needsInteraction(e)) {
        const delay = TRANSIENT_RETRY_DELAYS_MS[transientFailures.current]
        console.error(`[Auth] Silent token acquisition failed (attempt ${transientFailures.current + 1}):`, e)
        if (delay !== undefined) {
          // Keep the token we have — it may have a couple of minutes left —
          // and try again shortly.
          transientFailures.current += 1
          setRetryDelayMs(delay)
        } else {
          // Out of retries: the cached token is certainly dead by now, so stop
          // claiming to be logged in rather than letting every consumer
          // hammer the API with 401s until the next scheduled refresh. The
          // ladder starts over, so the scheduled refresh (or the button) still
          // gets a fair go at recovering.
          transientFailures.current = 0
          setAccessToken(null)
          setIsLoggedIn(false)
          setNotice(RENEWAL_FAILED_NOTICE)
        }
        return
      }
    }

    // Silent renewal is out of options: the SPA refresh token has expired and
    // the hidden-iframe fallback could not use the Microsoft session cookie.
    // Hand the browser to Microsoft rather than opening a popup — the refresh
    // timer has no user gesture behind it, so a popup would simply be blocked
    // and the app would sit there looking empty.
    setAccessToken(null)
    setIsLoggedIn(false)
    // Re-check and claim: two overlapping renewals (interval plus a scheduled
    // retry) can both have passed the guard at the top before either got here.
    if (interactiveAttempted.current) return
    interactiveAttempted.current = true
    setIsRedirecting(true)
    rememberReturnPath()

    try {
      await instance.acquireTokenRedirect({ scopes: [config.authScope], account: accounts[0] })
      // MSAL's default navigation client resolves after
      // system.redirectNavigationTimeout (30s) instead of throwing, so getting
      // here at all means the browser never left the page.
      console.error('[Auth] Interactive token redirect did not navigate.')
    } catch (redirectError) {
      console.error('[Auth] Interactive token redirect failed:', redirectError)
    }

    // Nobody is coming back to this path now, and App.tsx only clears it after
    // a successful sign-in, so drop it here rather than letting it hijack the
    // next one.
    forgetReturnPath()
    setIsRedirecting(false)
    setNotice(SESSION_EXPIRED_NOTICE)
  }, [config, accounts, instance, inProgress])

  const handleNoticeAction = useCallback(async () => {
    if (!notice) return

    if (notice.action === 'reload') {
      window.location.reload()
      return
    }

    if (notice.action === 'retry') {
      transientFailures.current = 0
      setNotice(null)
      fetchToken()
      return
    }

    // 'signIn' is a real user gesture, so an interactive redirect is allowed
    // again. Clear the cache first: leaving the stale account in place is what
    // made the old "/login" button useless, because AuthProvider immediately
    // re-entered the same failing silent flow.
    setIsRedirecting(true)
    rememberReturnPath()
    try {
      await instance.clearCache()
    } catch (cacheError) {
      // Not fatal: worst case Microsoft signs the stale account straight back
      // in, which is what the operator asked for anyway.
      console.error('[Auth] Could not clear the MSAL cache before signing in:', cacheError)
    }
    try {
      await instance.loginRedirect({ scopes: config ? [config.authScope] : [] })
      console.error('[Auth] Sign-in redirect did not navigate.')
    } catch (loginError) {
      console.error('[Auth] Sign-in redirect failed:', loginError)
    }
    forgetReturnPath()
    setIsRedirecting(false)
  }, [notice, instance, config, fetchToken])

  // Runs on mount and whenever MSAL settles. accounts is reference-stable
  // across renders (MsalProvider's reducer keeps the previous array when the
  // accounts are equal), so this does not spin.
  useEffect(() => {
    if (inProgress === InteractionStatus.None && accounts.length > 0) {
      fetchToken()
    }
  }, [inProgress, accounts, fetchToken])

  // The transient-failure retry ladder. Owning the timer in an effect keeps it
  // cleaned up on unmount and always runs the current fetchToken.
  useEffect(() => {
    if (retryDelayMs === null) return
    const timer = setTimeout(() => {
      setRetryDelayMs(null)
      fetchToken()
    }, retryDelayMs)
    return () => clearTimeout(timer)
  }, [retryDelayMs, fetchToken])

  useEffect(() => {
    const interval = setInterval(() => {
      fetchToken()
    }, REFRESH_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [fetchToken])

  // Renew just ahead of expiry instead of waiting for the next interval tick.
  useEffect(() => {
    if (!expiresOn) return
    const delay = Math.max(0, expiresOn.getTime() - Date.now() - RENEW_BEFORE_EXPIRY_MS)
    const timer = setTimeout(() => {
      fetchToken()
    }, delay)
    return () => clearTimeout(timer)
  }, [expiresOn, fetchToken])

  // Timers stall while the laptop sleeps or the tab is in the background, so
  // check the token as soon as the operator comes back. Cheap when it is
  // still valid: acquireTokenSilent answers from the cache.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchToken()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [fetchToken])

  // children stay mounted behind the overlay on purpose: AuthProvider sits
  // above the router, so returning early instead would unmount every page,
  // provider and half-filled form, and make /login unreachable.
  return (
    <AuthContext.Provider value={{ accessToken, isLoggedIn }}>
      {children}
      {isRedirecting && (
        <div className='tma-auth-overlay' role='status'>
          <AppLoading text='Your session expired, signing you in again…' />
        </div>
      )}
      {!isRedirecting && notice && (
        <SessionNotice title={notice.title} message={notice.message} actionText={notice.actionText} onAction={handleNoticeAction} />
      )}
    </AuthContext.Provider>
  )
}

// Come back to the page the operator was on; App.tsx restores this after MSAL
// returns. AuthProvider sits above the router, so read it off window.
const rememberReturnPath = () => {
  try {
    const here = `${window.location.pathname}${window.location.search}`
    if (here !== '/') sessionStorage.setItem(RETURN_PATH_KEY, here)
  } catch {
    // Storage unavailable; we simply land on the dashboard instead.
  }
}

const forgetReturnPath = () => {
  try {
    sessionStorage.removeItem(RETURN_PATH_KEY)
  } catch {
    // Storage unavailable; nothing was stored either.
  }
}

export const useAuth = () => useContext(AuthContext)
